import React, { useState, useEffect } from "react";
import styled from "styled-components";
import { COLOR_SYSTEM } from "../../design/design-system";
import Icon from "../../atoms/Icon";
import Text from "../../atoms/Text";
import Button from "../../atoms/Button";
import { ConfirmManager } from "../../organisms/CommonUI/ConfirmPopup/ConfirmManager";
import { RATING_LABELS } from "../../../common/models/types";
import { useFeedback, Rating } from "../../../contexts/FeedbackContext";
import { eventLog } from "../../../utils/AmplitudeUtil";

const FeedbackContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  padding: 16px;
  background: ${COLOR_SYSTEM.get("Gray")?.[0]};
  border-top: 1px solid ${COLOR_SYSTEM.get("Gray")?.[200]};

  @media print {
    display: none;
  }
`;

const StarRatingWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
`;

const StarContainer = styled.div`
  display: flex;
  gap: 4px;
  align-items: center;
`;

const RatingLabel = styled.span`
  font-size: 13px;
  color: ${COLOR_SYSTEM.get("Gray")?.[600]};
  height: 20px;
  transition: opacity 0.15s ease;
`;

const StarButton = styled.button<{
  $isActive: boolean;
  $isHovered: boolean;
  $isSubmitting: boolean;
}>`
  background: none;
  border: none;
  padding: 4px;
  cursor: ${({ $isSubmitting }) => ($isSubmitting ? "wait" : "pointer")};
  transition: transform 0.15s ease;

  &:hover {
    transform: scale(1.1);
  }

  &:disabled {
    cursor: not-allowed;
  }
`;

const DialogWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
`;

const DialogGuideGroup = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
`;

const DialogGuide = styled.p`
  margin: 0;
  text-align: center;
  font-size: 16px;
  color: ${COLOR_SYSTEM.get("Gray")?.[800]};
`;

const DialogHint = styled.p`
  margin: 0;
  text-align: center;
  font-size: 13px;
  color: ${COLOR_SYSTEM.get("Gray")?.[500]};
`;

const DialogStarRow = styled.div`
  display: flex;
  gap: 4px;
  align-items: center;
`;

const DialogStarButton = styled.button`
  background: none;
  border: none;
  padding: 4px;
  cursor: pointer;
  transition: transform 0.15s ease;

  &:hover {
    transform: scale(1.1);
  }

  &:disabled {
    cursor: not-allowed;
    transform: none;
  }
`;

const DialogRatingLabel = styled.span`
  font-size: 15px;
  font-weight: 500;
  color: ${COLOR_SYSTEM.get("Gray")?.[800]};
`;

const DialogButtonRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
  width: 100%;
  margin-top: 4px;
`;

interface LowRatingConfirmDialogProps {
  initialRating: Rating;
  reviewId?: number;
  onSubmit: (rating: Rating) => Promise<void>;
  onCancel: () => void;
}

const LowRatingConfirmDialog: React.FC<LowRatingConfirmDialogProps> = ({
  initialRating,
  reviewId,
  onSubmit,
  onCancel,
}) => {
  const [selected, setSelected] = useState<Rating>(initialRating);
  const [hovered, setHovered] = useState<Rating | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const display = (hovered ?? selected) as Rating;

  const handleSelect = (rating: Rating) => {
    if (rating !== selected) {
      eventLog("ai_review_feedback_low_rating_warning_rating_changed", {
        reviewId,
        from: selected,
        to: rating,
      });
    }
    setSelected(rating);
  };

  const handleSubmit = async () => {
    if (isSubmitting) return;
    setIsSubmitting(true);
    try {
      await onSubmit(selected);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <DialogWrapper>
      <DialogGuideGroup>
        <DialogGuide>선택하신 점수가 맞으신가요?</DialogGuide>
        <DialogHint>다른 점수를 원하시면 별을 눌러 바꿔주세요.</DialogHint>
      </DialogGuideGroup>
      <DialogStarRow>
        {Array.from({ length: 5 }, (_, index) => {
          const starValue = (index + 1) as Rating;
          const isActive = starValue <= display;
          return (
            <DialogStarButton
              key={starValue}
              type="button"
              onClick={() => handleSelect(starValue)}
              onMouseEnter={() => setHovered(starValue)}
              onMouseLeave={() => setHovered(null)}
              disabled={isSubmitting}
              aria-label={`${starValue}점`}
            >
              <Icon
                name="Star"
                size={28}
                color={
                  isActive
                    ? COLOR_SYSTEM.get("Yellow")?.[500]
                    : COLOR_SYSTEM.get("Gray")?.[300]
                }
              />
            </DialogStarButton>
          );
        })}
      </DialogStarRow>
      <DialogRatingLabel>
        {display}점 · {RATING_LABELS[display]}
      </DialogRatingLabel>
      <DialogButtonRow>
        <Button
          buttonType="button"
          color="Tertiary"
          size="Medium"
          type="Text"
          variant="Outlined"
          onClick={onCancel}
          disabled={isSubmitting}
        >
          닫기
        </Button>
        <Button
          buttonType="button"
          color="Primary"
          size="Medium"
          type="Text"
          variant="Contained"
          onClick={handleSubmit}
          disabled={isSubmitting}
        >
          이 점수로 제출
        </Button>
      </DialogButtonRow>
    </DialogWrapper>
  );
};

const SubmittedBadge = styled.button<{ $isClickable?: boolean }>`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 16px;
  background: ${COLOR_SYSTEM.get("Blue")?.[50]};
  border-radius: 6px;
  border: none;
  color: ${COLOR_SYSTEM.get("Blue")?.[600]};
  font-size: 14px;
  font-weight: 500;
  cursor: ${({ $isClickable }) => ($isClickable ? "pointer" : "default")};
  transition: all 0.2s ease;

  ${({ $isClickable }) =>
    $isClickable &&
    `
    &:hover {
      background: ${COLOR_SYSTEM.get("Blue")?.[100]};
      transform: translateY(-1px);
      box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
    }

    &:active {
      transform: translateY(0);
    }

    @media (prefers-reduced-motion: reduce) {
      &:hover {
        transform: none;
      }
      &:active {
        transform: none;
      }
    }
  `}
`;

interface AIReviewFeedbackRatingProps {
  reviewId?: number;
  onActivateFeedbackTab?: () => void;
}

const AIReviewFeedbackRating: React.FC<AIReviewFeedbackRatingProps> = ({
  reviewId,
  onActivateFeedbackTab,
}) => {
  const { state, actions, phase } = useFeedback();
  const [hoveredRating, setHoveredRating] = useState<number | null>(null);
  const [isBadgeHovered, setIsBadgeHovered] = useState(false);

  // reviewId 변경 시 상태 초기화
  // cleanup: 팝업 잔존 시 다른 리뷰에 1점이 박힐 수 있어 강제 정리
  useEffect(() => {
    setHoveredRating(null);
    setIsBadgeHovered(false);
    return () => {
      ConfirmManager.hide();
    };
  }, [reviewId]);

  const handleStarClick = async (rating: Rating) => {
    if (state.isSubmitting) return;

    if (rating === 1) {
      eventLog("ai_review_feedback_low_rating_warning_shown", { reviewId });
      ConfirmManager.show({
        title: "평가 점수 확인",
        body: (
          <LowRatingConfirmDialog
            initialRating={1}
            reviewId={reviewId}
            onSubmit={async (selectedRating) => {
              ConfirmManager.hide();
              await actions.submitRating(selectedRating);
            }}
            onCancel={() => ConfirmManager.hide()}
          />
        ),
      });
      return;
    }

    await actions.submitRating(rating);
  };

  const renderStars = (
    rating: number,
    size: number = 24,
    interactive: boolean = true
  ) => {
    return Array.from({ length: 5 }, (_, index) => {
      const starValue = (index + 1) as Rating;
      const isActive = interactive
        ? hoveredRating !== null
          ? starValue <= hoveredRating
          : false
        : starValue <= rating;

      if (interactive) {
        return (
          <StarButton
            key={starValue}
            $isActive={isActive}
            $isHovered={hoveredRating !== null && starValue <= hoveredRating}
            $isSubmitting={state.isSubmitting}
            onClick={() => handleStarClick(starValue)}
            onMouseEnter={() => setHoveredRating(starValue)}
            onMouseLeave={() => setHoveredRating(null)}
            disabled={state.isSubmitting}
            aria-label={`${starValue}점`}
          >
            <Icon
              name="Star"
              size={size}
              color={
                isActive
                  ? COLOR_SYSTEM.get("Yellow")?.[500]
                  : COLOR_SYSTEM.get("Gray")?.[300]
              }
            />
          </StarButton>
        );
      }

      return (
        <Icon
          key={starValue}
          name="Star"
          size={size}
          color={
            isActive
              ? COLOR_SYSTEM.get("Yellow")?.[500]
              : COLOR_SYSTEM.get("Gray")?.[300]
          }
        />
      );
    });
  };

  // phase === 'hidden', 'thankYou', 'adminView'면 컴포넌트 숨김
  if (phase === "hidden" || phase === "thankYou" || phase === "adminView") {
    return null;
  }

  // phase === 'detail': 별점 제출 완료, 상세 의견 입력 가능
  if (phase === "detail" && state.rating) {
    const isClickable = !!onActivateFeedbackTab;

    return (
      <FeedbackContainer data-exclude-from-save="true">
        <SubmittedBadge
          $isClickable={isClickable}
          onClick={isClickable ? onActivateFeedbackTab : undefined}
          onMouseEnter={() => isClickable && setIsBadgeHovered(true)}
          onMouseLeave={() => setIsBadgeHovered(false)}
        >
          <Icon name="Check" size={16} color={COLOR_SYSTEM.get("Blue")?.[600]} />
          {isBadgeHovered && isClickable ? "자세한 의견 남기기" : "피드백 제출 완료"}
        </SubmittedBadge>
      </FeedbackContainer>
    );
  }

  // phase === 'rating': 별점 입력 대기
  return (
    <FeedbackContainer data-exclude-from-save="true">
      <Text textType="Body2" color={COLOR_SYSTEM.get("Gray")?.[600]}>
        이 검토안에 만족하시나요?
      </Text>
      <StarRatingWrapper>
        <StarContainer>{renderStars(0, 28, true)}</StarContainer>
        <RatingLabel>
          {hoveredRating ? RATING_LABELS[hoveredRating] : "\u00A0"}
        </RatingLabel>
      </StarRatingWrapper>
    </FeedbackContainer>
  );
};

export default AIReviewFeedbackRating;
